import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { HandlingTierGroup, BulkR2FTableProps } from '../../types/bulkR2fTypes';
import { PendingThresholdChange, ValidationErrors } from '../../types/r2fTypes';
import { HandlingTierSection } from './HandlingTierSection';
import './BulkR2FTable.css';

// Filter state interface
interface FilterState {
    warehouseSearch: string;
    selectedProductionCapability: string;
}

/**
 * Main component for the Bulk R2F threshold table
 * Displays data organized by handling tiers with collapsible warehouse sections
 */
export const BulkR2FTable: React.FC<BulkR2FTableProps & {
    pendingChanges?: PendingThresholdChange[];
    onPendingChangesUpdate?: (changes: PendingThresholdChange[]) => void;
    userLogin?: string;
}> = ({
    data,
    onSave,
    userCanEdit,
    isLoading = false,
    pendingChanges = [],
    onPendingChangesUpdate,
    userLogin
}) => {
    // State for tracking which warehouses are expanded
    const [expandedWarehouses, setExpandedWarehouses] = useState<Record<string, boolean>>({});
    
    // Local state for pending changes (used when parent doesn't manage state)
    const [localPendingChanges, setLocalPendingChanges] = useState<Record<string, PendingThresholdChange>>({});
    
    // State for validation tracking - tracks invalid warehouse-PC combinations
    const [invalidRows, setInvalidRows] = useState<Set<string>>(new Set());
    
    // Use parent's pending changes if provided, otherwise maintain local state
    // This consolidates the dual state management pattern
    const effectivePendingChanges = useMemo(() => {
        if (onPendingChangesUpdate && pendingChanges.length > 0) {
            // Convert array to object for easier lookup when parent manages state
            return pendingChanges.reduce((acc, change) => {
                const key = `${change.warehouseId}-${change.productionCapability}-${change.handlingTier}`;
                acc[key] = change;
                return acc;
            }, {} as Record<string, PendingThresholdChange>);
        }
        return localPendingChanges;
    }, [pendingChanges, localPendingChanges, onPendingChangesUpdate]);

    // Validate threshold change function (similar to individual R2F table)
    const validateThresholdChange = useCallback((low: number, high: number): ValidationErrors => {
        return {
            lowHighInvalid: low >= high,
            negativeLowThreshold: low < 0,
            negativeHighThreshold: high < 0,
            duplicateHandlingTier: false // Not applicable for bulk R2F
        };
    }, []);

    // Check if a change is valid
    const isChangeValid = useCallback((low: number, high: number): boolean => {
        const validationResult = validateThresholdChange(low, high);
        return !(validationResult.lowHighInvalid ||
                validationResult.negativeLowThreshold ||
                validationResult.negativeHighThreshold);
    }, [validateThresholdChange]);

    // Update invalid rows whenever pendingChanges changes
    useEffect(() => {
        const newInvalidRows = new Set<string>();
        
        Object.entries(effectivePendingChanges).forEach(([changeKey, change]) => {
            if (!isChangeValid(change.lowThreshold, change.highThreshold)) {
                newInvalidRows.add(changeKey);
            }
        });
        
        setInvalidRows(newInvalidRows);
    }, [effectivePendingChanges, isChangeValid]);

    // Filter state
    const [filters, setFilters] = useState<FilterState>({
        warehouseSearch: '',
        selectedProductionCapability: ''
    });

    // Get all unique production capabilities for filter dropdown
    const availableProductionCapabilities = useMemo(() => {
        const pcSet = new Set<string>();
        data.forEach(tierGroup => {
            tierGroup.warehouses.forEach(warehouse => {
                warehouse.productionCapabilities.forEach(pc => {
                    pcSet.add(pc.name);
                });
            });
        });
        return Array.from(pcSet).sort();
    }, [data]);

    // Filter the data based on current filters
    const filteredData = useMemo(() => {
        // Pre-compute search terms for better performance
        const warehouseSearchLower = filters.warehouseSearch.toLowerCase();
        const hasWarehouseFilter = filters.warehouseSearch !== '';
        const hasPcFilter = filters.selectedProductionCapability !== '';

        return data.map(tierGroup => {
            const filteredWarehouses = tierGroup.warehouses.filter(warehouse => {
                // Early return for warehouse search filter
                if (hasWarehouseFilter && !warehouse.warehouseId.toLowerCase().includes(warehouseSearchLower)) {
                    return false;
                }

                // Early return for production capability filter
                if (hasPcFilter && !warehouse.productionCapabilities.some(pc => pc.name === filters.selectedProductionCapability)) {
                    return false;
                }

                return true;
            });

            return {
                ...tierGroup,
                warehouses: filteredWarehouses
            };
        }).filter(tierGroup => tierGroup.warehouses.length > 0);
    }, [data, filters.warehouseSearch, filters.selectedProductionCapability]);

    // Helper function to check if a production capability should be displayed
    const shouldShowProductionCapability = (pcName: string): boolean => {
        return filters.selectedProductionCapability === '' || pcName === filters.selectedProductionCapability;
    };

    // Handle filter changes
    const handleWarehouseSearchChange = (value: string) => {
        setFilters(prev => ({ ...prev, warehouseSearch: value }));
    };

    const handleProductionCapabilityChange = (value: string) => {
        setFilters(prev => ({ ...prev, selectedProductionCapability: value }));
    };

    // Clear all filters
    const handleClearFilters = () => {
        setFilters({
            warehouseSearch: '',
            selectedProductionCapability: ''
        });
    };

    // Check if any filters are active
    const hasActiveFilters = filters.warehouseSearch !== '' || filters.selectedProductionCapability !== '';

    // Handle toggling warehouse expansion
    const handleToggleExpand = (warehouseId: string) => {
        setExpandedWarehouses(prev => ({
            ...prev,
            [warehouseId]: !prev[warehouseId]
        }));
    };

    // Handle value changes
    const handleValueChange = ({
        warehouseId,
        productionCapability,
        field,
        value,
        tierName
    }: {
        warehouseId: string;
        productionCapability: string;
        field: 'lowThreshold' | 'highThreshold';
        value: number;
        tierName: string;
    }) => {
        if (!userCanEdit) return;

        const changeKey = `${warehouseId}-${productionCapability}-${tierName}`;
        const existingChange = effectivePendingChanges[changeKey];
        
        // Find the original PC data to get current values with proper null checks
        let originalPc: any = null;
        if (data && Array.isArray(data)) {
            for (const tierGroup of data) {
                if (tierGroup?.warehouses && Array.isArray(tierGroup.warehouses)) {
                    for (const warehouse of tierGroup.warehouses) {
                        if (warehouse?.warehouseId === warehouseId && warehouse.productionCapabilities) {
                            originalPc = warehouse.productionCapabilities.find(pc => pc?.name === productionCapability);
                            if (originalPc) break;
                        }
                    }
                }
                if (originalPc) break;
            }
        }

        // Add error handling if original PC data is not found
        if (!originalPc) {
            console.warn(`Could not find original PC data for ${warehouseId}-${productionCapability}`);
            return;
        }

        // Calculate the new threshold values
        const newLowThreshold = field === 'lowThreshold' ? value : (existingChange?.lowThreshold ?? originalPc?.lowThreshold ?? 0);
        const newHighThreshold = field === 'highThreshold' ? value : (existingChange?.highThreshold ?? originalPc?.highThreshold ?? 0);

        // Validate the new threshold combination
        if (!isChangeValid(newLowThreshold, newHighThreshold)) {
            console.warn(`Invalid threshold combination: low=${newLowThreshold}, high=${newHighThreshold} for ${warehouseId}-${productionCapability}`);
            // Still allow the change to be set, but it will be flagged as invalid
        }

        const newChange: PendingThresholdChange = {
            productionCapability,
            handlingTier: tierName, // Use actual handling tier instead of hardcoded "Default"
            lowThreshold: newLowThreshold,
            highThreshold: newHighThreshold,
            currentLowThreshold: originalPc?.lowThreshold ?? 0,
            currentHighThreshold: originalPc?.highThreshold ?? 0,
            action: 'Modify',
            isDefault: tierName === 'Default', // Only set isDefault=true for actual Default tier
            warehouseId,
            userAlias: userLogin || 'unknown-user',
            reason: 'Bulk R2F Update',
            actionSource: 'Newton Dashboard Bulk R2F'
        };

        // Update state based on whether parent manages state or we manage locally
        if (onPendingChangesUpdate) {
            // Parent manages state - notify parent of the change
            const currentChanges = pendingChanges.filter(change =>
                `${change.warehouseId}-${change.productionCapability}-${change.handlingTier}` !== changeKey
            );
            onPendingChangesUpdate([...currentChanges, newChange]);
        } else {
            // We manage state locally
            const updatedChanges = {
                ...localPendingChanges,
                [changeKey]: newChange
            };
            setLocalPendingChanges(updatedChanges);
        }
    };

    // Handle save button click
    const handleSave = async () => {
        // Check for invalid rows before attempting to save
        if (invalidRows.size > 0) {
            console.warn('Cannot save - invalid threshold combinations detected:', Array.from(invalidRows));
            return;
        }
        
        const changesToSave = Object.values(effectivePendingChanges);
        if (changesToSave.length === 0) {
            return;
        }
        
        try {
            await onSave(changesToSave);
            // Clear local state if we're managing it locally
            if (!onPendingChangesUpdate) {
                setLocalPendingChanges({});
            }
        } catch (error) {
            // Log error for debugging but let parent handle user-facing error messages
            console.error('Failed to save bulk TRB changes:', error);
            // Re-throw to allow parent component to handle user-facing error display
            throw error;
        }
    };

    // Handle expand/collapse all
    const handleExpandAll = () => {
        const allWarehouseIds: Record<string, boolean> = {};
        filteredData.forEach(tierGroup => {
            tierGroup.warehouses.forEach(warehouse => {
                allWarehouseIds[warehouse.warehouseId] = true;
            });
        });
        setExpandedWarehouses(allWarehouseIds);
    };

    const handleCollapseAll = () => {
        setExpandedWarehouses({});
    };

    const pendingChangeCount = Object.keys(effectivePendingChanges).length;
    const invalidRowCount = invalidRows.size;
    const hasValidationErrors = invalidRowCount > 0;

    return (
        <div className="bulk-r2f-table-container">
            {/* Table Header */}
            <div className="bulk-r2f-table-header">
                <div className="header-left">
                    <h3>TRB Thresholds by Handling Tier</h3>
                    <div className="expand-collapse-buttons">
                        <button
                            onClick={handleExpandAll}
                            className="expand-all-button"
                            title="Expand all warehouses"
                        >
                            Expand All
                        </button>
                        <button
                            onClick={handleCollapseAll}
                            className="collapse-all-button"
                            title="Collapse all warehouses"
                        >
                            Collapse All
                        </button>
                    </div>
                </div>
                {pendingChangeCount > 0 && (
                    <div className="pending-changes-info">
                        <div className="changes-summary">
                            <span>{pendingChangeCount} pending change{pendingChangeCount > 1 ? 's' : ''}</span>
                            {hasValidationErrors && (
                                <span className="validation-error-summary">
                                    ({invalidRowCount} invalid threshold combination{invalidRowCount > 1 ? 's' : ''})
                                </span>
                            )}
                        </div>
                        {userCanEdit && (
                            <button
                                onClick={handleSave}
                                disabled={isLoading || hasValidationErrors}
                                className={`save-changes-button ${hasValidationErrors ? 'disabled-validation' : ''}`}
                                title={hasValidationErrors ? 'Cannot save - fix invalid threshold combinations first' : ''}
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>
                )}
                {hasValidationErrors && (
                    <div className="validation-errors-info">
                        <span className="validation-error-icon">⚠️</span>
                        <span className="validation-error-text">
                            Some thresholds have invalid combinations where low threshold is greater than or equal to high threshold.
                            Please fix these before saving.
                        </span>
                    </div>
                )}
            </div>

            {/* Filter Controls */}
            <div className="bulk-r2f-filter-controls">
                <div className="filter-row">
                    <div className="filter-group">
                        <label htmlFor="warehouse-search" className="filter-label">
                            Warehouse Search:
                        </label>
                        <input
                            id="warehouse-search"
                            type="text"
                            placeholder="Search by warehouse ID (e.g., BWI, DFW)"
                            value={filters.warehouseSearch}
                            onChange={(e) => handleWarehouseSearchChange(e.target.value)}
                            className="filter-input warehouse-search-input"
                        />
                    </div>
                    
                    <div className="filter-group">
                        <label htmlFor="pc-filter" className="filter-label">
                            Production Capability:
                        </label>
                        <select
                            id="pc-filter"
                            value={filters.selectedProductionCapability}
                            onChange={(e) => handleProductionCapabilityChange(e.target.value)}
                            className="filter-select pc-filter-select"
                        >
                            <option value="">All PCs</option>
                            {availableProductionCapabilities.map(pc => (
                                <option key={pc} value={pc}>
                                    {pc}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-actions">
                        {hasActiveFilters && (
                            <button
                                onClick={handleClearFilters}
                                className="clear-filters-button"
                                title="Clear all filters"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Active Filter Indicators */}
                {hasActiveFilters && (
                    <div className="active-filters">
                        <span className="active-filters-label">Filtered by:</span>
                        {filters.warehouseSearch && (
                            <span className="filter-tag">
                                Warehouse: "{filters.warehouseSearch}"
                                <button
                                    onClick={() => handleWarehouseSearchChange('')}
                                    className="filter-tag-remove"
                                    title="Remove warehouse filter"
                                >
                                    ×
                                </button>
                            </span>
                        )}
                        {filters.selectedProductionCapability && (
                            <span className="filter-tag">
                                PC: "{filters.selectedProductionCapability}"
                                <button
                                    onClick={() => handleProductionCapabilityChange('')}
                                    className="filter-tag-remove"
                                    title="Remove PC filter"
                                >
                                    ×
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Tier Sections - Horizontal Columns */}
            <div className="tier-sections">
                <div className="tier-columns-container">
                    {filteredData.map((tierGroup: HandlingTierGroup) => (
                        <div key={tierGroup.tierName} className="tier-column">
                            <HandlingTierSection
                                tierGroup={tierGroup}
                                onToggleExpand={handleToggleExpand}
                                onValueChange={handleValueChange}
                                userCanEdit={userCanEdit}
                                pendingChanges={effectivePendingChanges}
                                expandedWarehouses={expandedWarehouses}
                                shouldShowProductionCapability={shouldShowProductionCapability}
                                invalidRows={invalidRows}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {filteredData.length === 0 && data.length > 0 && (
                <div className="no-data-message">
                    <p>No warehouses match the current filter criteria.</p>
                    <button onClick={handleClearFilters} className="clear-filters-link">
                        Clear filters to show all data
                    </button>
                </div>
            )}

            {data.length === 0 && (
                <div className="no-data-message">
                    <p>No TRB threshold data available for the selected warehouses.</p>
                </div>
            )}
        </div>
    );
};